import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { confirmEmailVerification } from "@/api/emailVerification";

type Status = "idle" | "verifying" | "success" | "error";

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Missing verification token");
      return;
    }
    setStatus("verifying");
    confirmEmailVerification(token)
      .then(() => {
        setStatus("success");
        setMessage("Your email has been verified.");
      })
      .catch((err: unknown) => {
        const axiosErr = err as { response?: { data?: { detail?: string } } };
        setStatus("error");
        setMessage(
          axiosErr.response?.data?.detail ||
            "Verification failed. The link may be expired or already used."
        );
      });
  }, [token]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm text-center"
      >
        <div className="mb-8">
          <img src="/logo.png" alt="Lenavio" className="h-9 w-auto mx-auto" />
        </div>

        {status === "verifying" && (
          <div className="rounded-lg bg-surface border border-border px-6 py-8">
            <Loader2 className="h-8 w-8 text-accent mx-auto mb-3 animate-spin" />
            <p className="text-sm text-text-primary font-medium">
              Verifying your email…
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="rounded-lg bg-success/10 border border-success/20 px-6 py-8">
            <CheckCircle2 className="h-10 w-10 text-success mx-auto mb-3" />
            <p className="text-sm text-text-primary font-medium">{message}</p>
            <Link
              to="/login"
              className="inline-block mt-4 text-sm text-accent hover:text-accent-600"
            >
              Continue to sign in
            </Link>
          </div>
        )}

        {status === "error" && (
          <div className="rounded-lg bg-danger/10 border border-danger/20 px-6 py-8">
            <XCircle className="h-10 w-10 text-danger mx-auto mb-3" />
            <p className="text-sm text-text-primary font-medium">
              Email verification failed
            </p>
            <p className="text-xs text-text-muted mt-1">{message}</p>
            <Link
              to="/login"
              className="inline-block mt-4 text-sm text-accent hover:text-accent-600"
            >
              Back to sign in
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
